import { NormsInfo } from '../types/NormsInfo';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  errorFields: string[]; // Field names that have errors
}

// Sentinel values that are allowed for special meanings
const SENTINEL_VALUES = [996, 997, 998, 999];

/**
 * Checks if a value is a valid sentinel value
 */
const isSentinelValue = (value: number): boolean => {
  return SENTINEL_VALUES.includes(value);
};

/**
 * Validates a field value against a valid range or sentinel values
 * @param value - The value to validate
 * @param min - Minimum valid value (inclusive)
 * @param max - Maximum valid value (inclusive)
 * @param fieldName - Name of the field for error messages
 * @returns Error message if invalid, null if valid
 */
const validateFieldRange = (
  value: number | null | undefined,
  min: number,
  max: number,
  fieldName: string
): string | null => {
  if (value === null || value === undefined) {
    return null; // Null/undefined handled by required field validation
  }

  // Allow sentinel values
  if (isSentinelValue(value)) {
    return null;
  }

  // Check if value is within valid range
  if (value < min || value > max) {
    return `${fieldName} must be between ${min} and ${max}.`;
  }

  return null;
};

/**
 * Converts MM/DD/YYYY format to yyyy-mm-dd format
 */
function convertToISOFormat(dateStr: string): string {
  if (!dateStr) return '';
  
  // If already in ISO format (yyyy-mm-dd), return as is
  if (dateStr.includes('-')) {
    return dateStr.split('T')[0];
  }
  
  // Convert from MM/DD/YYYY to yyyy-mm-dd
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const [month, day, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  return dateStr;
}

/**
 * Returns true if the date string (MM/DD/YYYY or yyyy-mm-dd or with time) is a valid calendar date.
 * Rejects invalid dates like Feb 30, Nov 31, Apr 31, etc.
 */
export function isValidCalendarDate(dateStr: string): boolean {
  if (!dateStr || typeof dateStr !== 'string') return false;
  
  // Convert to ISO format first
  const isoDate = convertToISOFormat(dateStr.trim());
  if (!isoDate) return false;
  
  const part = isoDate.split('T')[0];
  if (!part) return false;
  const parts = part.split('-');
  if (parts.length !== 3) return false;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return false;
  if (m < 1 || m > 12 || d < 1) return false;
  const date = new Date(y, m - 1, d);
  return (
    date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d
  );
}

/**
 * Validates required fields for a specific tab
 */
export const validateTab = (tabId: string, normsInfo: NormsInfo): ValidationResult => {
  const errors: string[] = [];
  const errorFields: string[] = [];

  switch (tabId) {
    case 'general':
      if (!normsInfo.firstname || normsInfo.firstname.trim() === '') {
        errors.push('First Name is required');
        errorFields.push('firstname');
      }
      if (!normsInfo.lastname || normsInfo.lastname.trim() === '') {
        errors.push('Last Name is required');
        errorFields.push('lastname');
      }
      if (!normsInfo.sex || normsInfo.sex.trim() === '') {
        errors.push('Gender is required');
        errorFields.push('sex');
      }
      if (!normsInfo.race || normsInfo.race.trim() === '') {
        errors.push('Native (Race) is required');
        errorFields.push('race');
      }
      if (normsInfo.education === undefined || normsInfo.education === null || normsInfo.education === 0) {
        errors.push('Education (Years) is required');
        errorFields.push('education');
      }
      if (typeof normsInfo.education === 'number' && normsInfo.education > 20) {
        errors.push('Education cannot exceed 20 years');
        errorFields.push('education');
      }
      if (!normsInfo.referralsource || normsInfo.referralsource.trim() === '') {
        errors.push('Referred By is required');
        errorFields.push('referralsource');
      }
      if (!normsInfo.handedness || normsInfo.handedness.trim() === '') {
        errors.push('Handedness is required');
        errorFields.push('handedness');
      }
      if (!normsInfo.testingdate || normsInfo.testingdate.trim() === '') {
        errors.push('Testing Date is required');
        errorFields.push('testingdate');
      }
      if (!normsInfo.dob || normsInfo.dob.trim() === '') {
        errors.push('Date of Birth is required');
        errorFields.push('dob');
      }
      if (normsInfo.dob && normsInfo.testingdate) {
        const dobISO = convertToISOFormat(normsInfo.dob);
        const testingISO = convertToISOFormat(normsInfo.testingdate);
        const dobDate = new Date(dobISO);
        const testingDate = new Date(testingISO);
        if (!isNaN(dobDate.getTime()) && !isNaN(testingDate.getTime()) && dobDate > testingDate) {
          errors.push('Date of Birth cannot be after Date of Testing');
          if (!errorFields.includes('dob')) errorFields.push('dob');
          if (!errorFields.includes('testingdate')) errorFields.push('testingdate');
        }
      }
       if (normsInfo.dob && normsInfo.dob.trim() !== '' && !isValidCalendarDate(normsInfo.dob)) {
         errors.push('Date of Birth must be a valid calendar date');
        if (!errorFields.includes('dob')) errorFields.push('dob');
       }
      if (normsInfo.testingdate && normsInfo.testingdate.trim() !== '' && !isValidCalendarDate(normsInfo.testingdate)) {
        errors.push('Date of Testing must be a valid calendar date');
        if (!errorFields.includes('testingdate')) errorFields.push('testingdate');
      }
      if (normsInfo.testingdate && normsInfo.testingdate.trim() !== '' && isValidCalendarDate(normsInfo.testingdate)) {
        const testingISO = convertToISOFormat(normsInfo.testingdate);
        const testingDate = new Date(testingISO);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        testingDate.setHours(0, 0, 0, 0);
        if (testingDate > today) {
          errors.push('Date of Testing cannot be a future date');
          if (!errorFields.includes('testingdate')) errorFields.push('testingdate');
        }
      }
      if (normsInfo.fuv && normsInfo.fuv.trim() !== '' && !isValidCalendarDate(normsInfo.fuv)) {
        errors.push('Date of Follow Up must be a valid calendar date');
        if (!errorFields.includes('fuv')) errorFields.push('fuv');
      }
      if (normsInfo.fuv && normsInfo.fuv.trim() !== '' && isValidCalendarDate(normsInfo.fuv)) {
        const fuvISO = convertToISOFormat(normsInfo.fuv);
        const fuvDate = new Date(fuvISO);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        fuvDate.setHours(0, 0, 0, 0);
        if (fuvDate < today) {
          errors.push('Date of Follow Up cannot be a past date');
          if (!errorFields.includes('fuv')) errorFields.push('fuv');
        }
      }
      const hasPlaceId =
        normsInfo.placeTestingId !== undefined &&
        normsInfo.placeTestingId !== null &&
        Number(normsInfo.placeTestingId) > 0;
      if (!hasPlaceId && (!normsInfo.place || normsInfo.place.trim() === '')) {
        errors.push('Testing Location is required');
        errorFields.push('place');
      }
      if (!normsInfo.occupation || normsInfo.occupation.trim() === '') {
        errors.push('Occupation is required');
        errorFields.push('occupation');
      }

      break;

    case 'screening':
      // Check for the appropriate test type based on selection
      const screeningTestType = normsInfo.screeningTestType || 'MOCA';
      
      if (screeningTestType === 'MMSE') {
        if (normsInfo.mmse === undefined || normsInfo.mmse === null) {
          errors.push('MMSE is required');
          errorFields.push('mmse');
        } else {
          // Validate MMSE range: 0-30 or sentinel values (996-999)
          const mmseError = validateFieldRange(normsInfo.mmse, 0, 30, 'MMSE');
          if (mmseError) {
            errors.push(mmseError);
            errorFields.push('mmse');
          }
        }
      } else {
        if (normsInfo.moca === undefined || normsInfo.moca === null) {
          errors.push('MOCA is required');
          errorFields.push('moca');
        } else {
          // Validate MOCA range: 0-30 or sentinel values (996-999)
          const mocaError = validateFieldRange(normsInfo.moca, 0, 30, 'MOCA');
          if (mocaError) {
            errors.push(mocaError);
            errorFields.push('moca');
          }
        }
      }
      
      // Orientation fields - only required check, no range validation
      if (normsInfo.o_person === undefined || normsInfo.o_person === null) {
        errors.push('Orientation - Person is required');
        errorFields.push('o_person');
      }
      
      if (normsInfo.o_place === undefined || normsInfo.o_place === null) {
        errors.push('Orientation - Place is required');
        errorFields.push('o_place');
      }
      
      if (normsInfo.o_time === undefined || normsInfo.o_time === null) {
        errors.push('Orientation - Time is required');
        errorFields.push('o_time');
      }
      
      // Determine which premorbid test type is being used
      const premorbidTestType = (normsInfo.premorbidTestType || 'AMNART').toUpperCase();
      const isTAP = premorbidTestType === 'TAP';

      if (isTAP) {
        if (normsInfo.tap === undefined || normsInfo.tap === null) {
          errors.push('TAP is required');
          errorFields.push('tap');
        } else {
          // TAP Raw Score # Correct: 10-30 per mapping table (sentinel 996-999 allowed)
          const tapError = validateFieldRange(normsInfo.tap, 10, 30, 'TAP (Raw Score # Correct)');
          if (tapError) {
            errors.push(tapError);
            errorFields.push('tap');
          }
        }
      } else {
        if (normsInfo.amnarterrors === undefined || normsInfo.amnarterrors === null) {
          errors.push('AMNART Errors is required');
          errorFields.push('amnarterrors');
        } else {
          // Validate AMNART Errors: 0-994 or sentinel values (996-999)
          // Note: 995 is reserved as sentinel in quarantine function, so max is 994
          const amnartError = validateFieldRange(normsInfo.amnarterrors, 0, 994, 'AMNART Errors');
          if (amnartError) {
            errors.push(amnartError);
            errorFields.push('amnarterrors');
          }
        }
      }
      break;

    case 'adas': {
      // Required fields validation
      if (normsInfo.wrecallt1 === undefined || normsInfo.wrecallt1 === null) {
        errors.push('Word Recall Trial 1 Errors is required');
        errorFields.push('wrecallt1');
      }
      if (normsInfo.wrecallt2 === undefined || normsInfo.wrecallt2 === null) {
        errors.push('Word Recall Trial 2 Errors is required');
        errorFields.push('wrecallt2');
      }
      if (normsInfo.wrecallt3 === undefined || normsInfo.wrecallt3 === null) {
        errors.push('Word Recall Trial 3 Errors is required');
        errorFields.push('wrecallt3');
      }
      if (normsInfo.intrusions === undefined || normsInfo.intrusions === null) {
        errors.push('Intrusions is required');
        errorFields.push('intrusions');
      }
      if (normsInfo.delayedrecall === undefined || normsInfo.delayedrecall === null) {
        errors.push('Delayed Recall is required');
        errorFields.push('delayedrecall');
      }
      if (normsInfo.orientation === undefined || normsInfo.orientation === null) {
        errors.push('Orientation is required');
        errorFields.push('orientation');
      }
      if (normsInfo.commands === undefined || normsInfo.commands === null) {
        errors.push('Commands is required');
        errorFields.push('commands');
      }
      if (normsInfo.namingfingers === undefined || normsInfo.namingfingers === null) {
        errors.push('Naming - Fingers is required');
        errorFields.push('namingfingers');
      }
      if (normsInfo.namingobjects === undefined || normsInfo.namingobjects === null) {
        errors.push('Naming - Objects is required');
        errorFields.push('namingobjects');
      }
      if (normsInfo.construction === undefined || normsInfo.construction === null) {
        errors.push('Construction is required');
        errorFields.push('construction');
      }
      if (normsInfo.missestrial1 === undefined || normsInfo.missestrial1 === null) {
        errors.push('Trial 1 - Misses is required');
        errorFields.push('missestrial1');
      }
      if (normsInfo.missestrial2 === undefined || normsInfo.missestrial2 === null) {
        errors.push('Trial 2 - Misses is required');
        errorFields.push('missestrial2');
      }
      if (normsInfo.missestrial3 === undefined || normsInfo.missestrial3 === null) {
        errors.push('Trial 3 - Misses is required');
        errorFields.push('missestrial3');
      }
      if (normsInfo.falsepositives1 === undefined || normsInfo.falsepositives1 === null) {
        errors.push('Trial 1 - False Positives is required');
        errorFields.push('falsepositives1');
      }
      if (normsInfo.falsepositives2 === undefined || normsInfo.falsepositives2 === null) {
        errors.push('Trial 2 - False Positives is required');
        errorFields.push('falsepositives2');
      }
      if (normsInfo.falsepositives3 === undefined || normsInfo.falsepositives3 === null) {
        errors.push('Trial 3 - False Positives is required');
        errorFields.push('falsepositives3');
      }
      if (normsInfo.ideationalpraxis === undefined || normsInfo.ideationalpraxis === null) {
        errors.push('Ideational Praxis is required');
        errorFields.push('ideationalpraxis');
      }
      if (normsInfo.speechcontent === undefined || normsInfo.speechcontent === null) {
        errors.push('Speech Content is required');
        errorFields.push('speechcontent');
      }
      if (normsInfo.wordfinding === undefined || normsInfo.wordfinding === null) {
        errors.push('Word Finding is required');
        errorFields.push('wordfinding');
      }
      if (normsInfo.auralcomprehension === undefined || normsInfo.auralcomprehension === null) {
        errors.push('Aural Comprehension is required');
        errorFields.push('auralcomprehension');
      }
      if (normsInfo.recalltestinstructions === undefined || normsInfo.recalltestinstructions === null) {
        errors.push('Recall Test Instructions is required');
        errorFields.push('recalltestinstructions');
      }

      // Range validation for ADAS fields
      // Word Recall Errors: 0-10 per trial (ADAS uses 10 words)
      const wordRecallFields: Array<{ key: keyof NormsInfo; min: number; max: number; label: string }> = [
        { key: 'wrecallt1', min: 0, max: 10, label: 'Word Recall Trial 1 Errors' },
        { key: 'wrecallt2', min: 0, max: 10, label: 'Word Recall Trial 2 Errors' },
        { key: 'wrecallt3', min: 0, max: 10, label: 'Word Recall Trial 3 Errors' },
      ];

      for (const { key, min, max, label } of wordRecallFields) {
        const value = normsInfo[key];
        if (value !== undefined && value !== null && value !== '') {
          const numVal = typeof value === 'number' ? value : Number(value);
          if (!Number.isNaN(numVal)) {
            const err = validateFieldRange(numVal, min, max, label);
            if (err) {
              errors.push(err);
              errorFields.push(key as string);
            }
          }
        }
      }

      // Word Recognition - Misses: 0-12 per trial (12 words in recognition)
      const missesFields: Array<{ key: keyof NormsInfo; min: number; max: number; label: string }> = [
        { key: 'missestrial1', min: 0, max: 12, label: 'Trial 1 - Misses' },
        { key: 'missestrial2', min: 0, max: 12, label: 'Trial 2 - Misses' },
        { key: 'missestrial3', min: 0, max: 12, label: 'Trial 3 - Misses' },
      ];

      for (const { key, min, max, label } of missesFields) {
        const value = normsInfo[key];
        if (value !== undefined && value !== null && value !== '') {
          const numVal = typeof value === 'number' ? value : Number(value);
          if (!Number.isNaN(numVal)) {
            const err = validateFieldRange(numVal, min, max, label);
            if (err) {
              errors.push(err);
              errorFields.push(key as string);
            }
          }
        }
      }

      // Word Recognition - False Positives: 0-12 per trial
      const falsePositivesFields: Array<{ key: keyof NormsInfo; min: number; max: number; label: string }> = [
        { key: 'falsepositives1', min: 0, max: 12, label: 'Trial 1 - False Positives' },
        { key: 'falsepositives2', min: 0, max: 12, label: 'Trial 2 - False Positives' },
        { key: 'falsepositives3', min: 0, max: 12, label: 'Trial 3 - False Positives' },
      ];

      for (const { key, min, max, label } of falsePositivesFields) {
        const value = normsInfo[key];
        if (value !== undefined && value !== null && value !== '') {
          const numVal = typeof value === 'number' ? value : Number(value);
          if (!Number.isNaN(numVal)) {
            const err = validateFieldRange(numVal, min, max, label);
            if (err) {
              errors.push(err);
              errorFields.push(key as string);
            }
          }
        }
      }

      // Naming Fields: Fingers 0-5, Objects 0-12
      const namingFields: Array<{ key: keyof NormsInfo; min: number; max: number; label: string }> = [
        { key: 'namingfingers', min: 0, max: 5, label: 'Naming - Fingers' },
        { key: 'namingobjects', min: 0, max: 12, label: 'Naming - Objects' },
      ];

      for (const { key, min, max, label } of namingFields) {
        const value = normsInfo[key];
        if (value !== undefined && value !== null && value !== '') {
          const numVal = typeof value === 'number' ? value : Number(value);
          if (!Number.isNaN(numVal)) {
            const err = validateFieldRange(numVal, min, max, label);
            if (err) {
              errors.push(err);
              errorFields.push(key as string);
            }
          }
        }
      }

      // Other ADAS Components: 0-5 or 0-8 depending on component
      const otherComponentsFields: Array<{ key: keyof NormsInfo; min: number; max: number; label: string }> = [
        { key: 'orientation', min: 0, max: 8, label: 'Orientation' },
        { key: 'commands', min: 0, max: 5, label: 'Commands' },
        { key: 'construction', min: 0, max: 5, label: 'Construction' },
        { key: 'ideationalpraxis', min: 0, max: 5, label: 'Ideational Praxis' },
        { key: 'speechcontent', min: 0, max: 5, label: 'Speech Content' },
        { key: 'auralcomprehension', min: 0, max: 5, label: 'Aural Comprehension' },
        { key: 'wordfinding', min: 0, max: 5, label: 'Word Finding' },
        { key: 'recalltestinstructions', min: 0, max: 5, label: 'Recall Test Instructions' },
        { key: 'intrusions', min: 0, max: 10, label: 'Intrusions' },
        { key: 'delayedrecall', min: 0, max: 10, label: 'Delayed Recall' },
      ];

      for (const { key, min, max, label } of otherComponentsFields) {
        const value = normsInfo[key];
        if (value !== undefined && value !== null && value !== '') {
          const numVal = typeof value === 'number' ? value : Number(value);
          if (!Number.isNaN(numVal)) {
            const err = validateFieldRange(numVal, min, max, label);
            if (err) {
              errors.push(err);
              errorFields.push(key as string);
            }
          }
        }
      }

      break;
    }

    case 'intellectual': {
      // No required fields for Intellectual tab
      break;
    }

    case 'language': {
      // Required fields: naming test (BNT vs EOWPVT) and verbal fluency (FAS vs PMR) are independent
      const languageTestType = (normsInfo.languageTestType || 'BNT').toUpperCase();
      const isEOWPVT = languageTestType === 'EOWPVT';
      const verbalFluencyType = (normsInfo.languageVerbalFluencyType || 'FAS').toUpperCase();
      const isPMR = verbalFluencyType === 'PMR';

      const requiredLanguageFields: Array<{ key: keyof NormsInfo; label: string }> = [];

      if (isEOWPVT) {
        requiredLanguageFields.push({ key: 'eowpvt', label: 'EOWPVT' });
      } else {
        requiredLanguageFields.push(
          { key: 'bnt', label: 'BNT (Uncued + Stimulus Cue)' },
          { key: 'bntcued', label: 'BNT (Cued with Phonemic Cue)' }
        );
      }

      if (isPMR) {
        requiredLanguageFields.push(
          { key: 'fas_p', label: 'PMR - P' },
          { key: 'fas_m', label: 'PMR - M' },
          { key: 'fas_r', label: 'PMR - R' }
        );
      } else {
        requiredLanguageFields.push(
          { key: 'f', label: 'FAS - F' },
          { key: 'a', label: 'FAS - A' },
          { key: 's', label: 'FAS - S' }
        );
      }

      // Common required fields (always required)
      requiredLanguageFields.push(
        { key: 'animals', label: 'Animals' },
        { key: 'wabyesno', label: 'WAB Yes/No' },
        { key: 'wabseqcom', label: 'WAB Sequential Commands' }
      );

      for (const { key, label } of requiredLanguageFields) {
        const value = normsInfo[key];
        if (value === undefined || value === null || value === '') {
          errors.push(`${label} is required`);
          errorFields.push(key as string);
        }
      }

      // Range validation for Language tab fields.
      // Ranges are chosen from the underlying norms JSON data and test structure:
      // - BNT raw scores: 0–60 (60 items; see Norm123 / BNTNorms.json usage)
      // - F, A, S, P, M, R: 0–40 (safe upper bound for letter fluency)
      // - Animals: 0–40 (safe upper bound for category fluency)
      // - WAB Yes/No and Sequential Commands: bounded positive counts; use conservative upper caps.
      const languageRangeFields: Array<{ key: keyof NormsInfo; min: number; max: number; label: string }> = [];

      if (isEOWPVT) {
        languageRangeFields.push({ key: 'eowpvt', min: 0, max: 200, label: 'EOWPVT' });
      } else {
        languageRangeFields.push(
          { key: 'bnt', min: 0, max: 60, label: 'BNT (Uncued + Stimulus Cue)' },
          { key: 'bntcued', min: 0, max: 60, label: 'BNT (Cued with Phonemic Cue)' }
        );
      }
      if (isPMR) {
        languageRangeFields.push(
          { key: 'fas_p', min: 0, max: 40, label: 'PMR - P' },
          { key: 'fas_m', min: 0, max: 40, label: 'PMR - M' },
          { key: 'fas_r', min: 0, max: 40, label: 'PMR - R' }
        );
      } else {
        languageRangeFields.push(
          { key: 'f', min: 0, max: 40, label: 'FAS - F' },
          { key: 'a', min: 0, max: 40, label: 'FAS - A' },
          { key: 's', min: 0, max: 40, label: 'FAS - S' }
        );
      }

      // Common range fields (always validated)
      languageRangeFields.push(
        { key: 'animals', min: 0, max: 40, label: 'Animals' },
        { key: 'wabyesno', min: 0, max: 60, label: 'WAB Yes/No' },
        { key: 'wabseqcom', min: 0, max: 80, label: 'WAB Sequential Commands' }
      );

      for (const { key, min, max, label } of languageRangeFields) {
        const value = normsInfo[key];
        if (value !== undefined && value !== null && value !== '') {
          const numVal = typeof value === 'number' ? value : Number(value);
          if (!Number.isNaN(numVal)) {
            const err = validateFieldRange(numVal, min, max, label);
            if (err) {
              errors.push(err);
              errorFields.push(key as string);
            }
          }
        }
      }

      break;
    }

    case 'listlearning': {
      // HVLT (Hopkins Verbal Learning Test) – range-only validation.
      // Fields are optional; when present they must fall in valid ranges.
      //
      // Based on HVLT-R list size and recognition indices:
      // - Trials 1–4: 0–12
      // - True +: 0–12
      // - False +: 0–12
      const hvltFields: Array<{ key: keyof NormsInfo; min: number; max: number; label: string }> = [
        { key: 'hvltr1', min: 0, max: 12, label: 'HVLT Trial 1' },
        { key: 'hvltr2', min: 0, max: 12, label: 'HVLT Trial 2' },
        { key: 'hvltr3', min: 0, max: 12, label: 'HVLT Trial 3' },
        { key: 'hvltr4', min: 0, max: 12, label: 'HVLT Trial 4 (Delayed)' },
        { key: 'hvltrtp', min: 0, max: 12, label: 'HVLT True +' },
        { key: 'hvltrfp', min: 0, max: 12, label: 'HVLT False +' },
      ];

      for (const { key, min, max, label } of hvltFields) {
        const value = normsInfo[key];
        if (value !== undefined && value !== null && value !== '') {
          const numVal = typeof value === 'number' ? value : Number(value);
          if (!Number.isNaN(numVal)) {
            const err = validateFieldRange(numVal, min, max, label);
            if (err) {
              errors.push(err);
              errorFields.push(key as string);
            }
          }
        }
      }

      break;
    }

    case 'wmsr': {
      // WMS-IV raw scores are validated in the backend using age- and variant-specific norms.
      // Frontend only enforces numeric input (and upper bound) in WMSRTab; no additional range checks here.
      break;
    }

    case 'personality': {
      // Range-only validation when value is present (same pattern as MOCA: 0–max or sentinel 996–999)
      const personalityFields: Array<{ key: keyof NormsInfo; max: number; label: string }> = [
        { key: 'gds', max: 30, label: 'Geriatric Depression Scale' },
        { key: 'geriatricanxietyscale', max: 75, label: 'Geriatric Anxiety Scale' },
        { key: 'geriatricanxietyinventory', max: 20, label: 'Geriatric Anxiety Inventory' },
        { key: 'beckdepressioninventory', max: 63, label: 'Beck Depression Inventory-II' },
        { key: 'beckanxietyinventory', max: 63, label: 'Beck Anxiety Inventory' },
        { key: 'pcl5', max: 80, label: 'PCL-5' },
        { key: 'npiqseverity', max: 36, label: 'NPI-Q Severity' },
        { key: 'npiqdistress', max: 60, label: 'NPI-Q Distress' },
      ];
      for (const { key, max, label } of personalityFields) {
        const value = normsInfo[key];
        if (value !== undefined && value !== null && value !== '') {
          const numVal = typeof value === 'number' ? value : Number(value);
          if (!Number.isNaN(numVal)) {
            // For bimanual fields we only require non-negative (no upper cap) plus sentinel allowance.
            if (key === 'bimanualerrors' || key === 'bimanualsequences') {
              if (!isSentinelValue(numVal) && numVal < 0) {
                const errMsg = `${label} must be 0 or greater, or use sentinel values (996, 997, 998, 999)`;
                errors.push(errMsg);
                errorFields.push(key as string);
              }
            } else {
              const err = validateFieldRange(numVal, 0, max, label);
              if (err) {
                errors.push(err);
                errorFields.push(key as string);
              }
            }
          }
        }
      }
      break;
    }

    case 'adls': {
      // Range-only validation when value is present
      const adlFields: Array<{ key: keyof NormsInfo; max: number; label: string }> = [
        // PSMS (max 5 each, total 30)
        { key: 'toilet', max: 5, label: 'Toilet' },
        { key: 'feeding', max: 5, label: 'Feeding' },
        { key: 'dressing', max: 5, label: 'Dressing' },
        { key: 'grooming', max: 5, label: 'Grooming' },
        { key: 'ambulation', max: 5, label: 'Ambulation' },
        { key: 'bathing', max: 5, label: 'Bathing' },
        // IADL (varied max values)
        { key: 'telephone', max: 5, label: 'Telephone' },
        { key: 'shopping', max: 4, label: 'Shopping' },
        { key: 'foodprep', max: 4, label: 'Food Preparation' },
        { key: 'housekeeping', max: 5, label: 'Housekeeping' },
        { key: 'laundry', max: 3, label: 'Laundry' },
        { key: 'transportation', max: 4, label: 'Transportation' },
        { key: 'finances', max: 3, label: 'Finances' },
        { key: 'medications', max: 3, label: 'Medications' },
      ];

      for (const { key, max, label } of adlFields) {
        const value = normsInfo[key];
        if (value !== undefined && value !== null && value !== '') {
          const numVal = typeof value === 'number' ? value : Number(value);
          if (!Number.isNaN(numVal)) {
            const err = validateFieldRange(numVal, 0, max, label);
            if (err) {
              errors.push(err);
              errorFields.push(key as string);
            }
          }
        }
      }
      break;
    }

    case 'visuospatial': {
      // Range-only validation when value is present
      const visuospatialFields: Array<{ key: keyof NormsInfo; max: number; label: string }> = [
        { key: 'cloxi', max: 15, label: 'CLOX I' },
        { key: 'cloxii', max: 15, label: 'CLOX II' },
        { key: 'beery', max: 30, label: 'Beery VMI' },
        { key: 'rey', max: 36, label: 'Rey Copy' },
      ];

      for (const { key, max, label } of visuospatialFields) {
        const value = normsInfo[key];
        if (value !== undefined && value !== null && value !== '') {
          const numVal = typeof value === 'number' ? value : Number(value);
          if (!Number.isNaN(numVal)) {
            const err = validateFieldRange(numVal, 0, max, label);
            if (err) {
              errors.push(err);
              errorFields.push(key as string);
            }
          }
        }
      }
      break;
    }

    case 'attention': {
      // Stroop test validation - using backend values (200/150/120)
      const stroopFields: Array<{ key: keyof NormsInfo; min: number; max: number; label: string }> = [
        { key: 'word', min: 0, max: 200, label: 'Stroop Word raw score' },
        { key: 'color', min: 0, max: 150, label: 'Stroop Color raw score' },
        { key: 'colorword', min: 0, max: 120, label: 'Stroop Color-Word raw score' },
      ];

      for (const { key, min, max, label } of stroopFields) {
        const value = normsInfo[key];
        if (value !== undefined && value !== null && value !== '') {
          const numVal = typeof value === 'number' ? value : Number(value);
          if (!Number.isNaN(numVal)) {
            const err = validateFieldRange(numVal, min, max, label);
            if (err) {
              errors.push(err);
              errorFields.push(key as string);
            }
          }
        }
      }

      break;
    }

    case 'motor': {
      // Range validation with additional constraint: bimanual errors <= sequences (when both numeric and not sentinel)
      const motorFields: Array<{ key: keyof NormsInfo; max: number; label: string }> = [
        { key: 'motorinhibition', max: 16, label: 'Motor Inhibition Errors' },
        // For bimanual, there is no fixed max cap (other than general numeric/sentinel rules);
        // we only enforce non-negative and the logical relationship errors <= sequences.
        { key: 'bimanualerrors', max: 999, label: 'Bimanual Errors' },
        { key: 'bimanualsequences', max: 999, label: 'Bimanual Sequences Completed' },
      ];

      const valuesCache: Record<string, number | undefined> = {};

      for (const { key, max, label } of motorFields) {
        const value = normsInfo[key];
        if (value !== undefined && value !== null && value !== '') {
          const numVal = typeof value === 'number' ? value : Number(value);
          valuesCache[key as string] = Number.isNaN(numVal) ? undefined : numVal;
          if (!Number.isNaN(numVal)) {
            const err = validateFieldRange(numVal, 0, max, label);
            if (err) {
              errors.push(err);
              errorFields.push(key as string);
            }
          }
        }
      }

      // Bimanual logical check: errors must not exceed sequences (unless either is a sentinel)
      const errorsVal = valuesCache['bimanualerrors'];
      const seqVal = valuesCache['bimanualsequences'];
      const errorsIsSentinel = errorsVal !== undefined && isSentinelValue(errorsVal);
      const seqIsSentinel = seqVal !== undefined && isSentinelValue(seqVal);

      if (
        errorsVal !== undefined &&
        seqVal !== undefined &&
        !Number.isNaN(errorsVal) &&
        !Number.isNaN(seqVal) &&
        !errorsIsSentinel &&
        !seqIsSentinel &&
        errorsVal > seqVal
      ) {
        const msg = 'Bimanual Errors must be less than or equal to sequences completed';
        errors.push(msg);
        if (!errorFields.includes('bimanualerrors')) errorFields.push('bimanualerrors');
        if (!errorFields.includes('bimanualsequences')) errorFields.push('bimanualsequences');
      }
      break;
    }

    default:
      // Other tabs don't have required fields based on WinForms validation
      break;
  }

  return {
    isValid: errors.length === 0,
    errors,
    errorFields,
  };
};
